import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, Send, Layers, ArrowDown, Minus, ArrowUp } from "lucide-react";

export const TIERS = [
  { id: "below", label: "Below grade", short: "Below", Icon: ArrowDown, color: "#0284C7", blurb: "Simpler words, one-step directions, extra hints." },
  { id: "on", label: "On grade", short: "On", Icon: Minus, color: "#0D9488", blurb: "The original lesson, just as written." },
  { id: "above", label: "Above grade", short: "Above", Icon: ArrowUp, color: "#7A3FF2", blurb: "Richer vocabulary, multi-step thinking, less scaffolding." },
];

export const TIER_BY_ID = Object.fromEntries(TIERS.map((t) => [t.id, t]));

export default function TierPickerModal({ group, onClose, onPreview, onAssign }) {
  return (
    <AnimatePresence>
      {group && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F2937]/60 p-4 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl rounded-[28px] border border-[#E5DECF] bg-white p-6 shadow-2xl" data-testid="tier-picker">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#7A3FF2]"><Layers size={14} /> Differentiated lesson</div>
                <h3 className="mt-1 font-heading text-xl font-extrabold text-[#1F2937]">{group.on?.title}</h3>
                <p className="mt-0.5 font-body text-sm text-[#6B7280]">Same objective, three levels. Preview each tier or assign it to the right students.</p>
              </div>
              <button onClick={onClose} data-testid="tier-picker-close" className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={20} /></button>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {TIERS.map((t) => {
                const l = group[t.id];
                return (
                  <div key={t.id} data-testid={`tier-card-${t.id}`} className="flex flex-col rounded-2xl border-2 p-4" style={{ borderColor: `${t.color}55`, background: `${t.color}0D` }}>
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-xl text-white" style={{ background: t.color }}><t.Icon size={16} strokeWidth={2.5} /></span>
                      <span className="font-heading text-sm font-extrabold" style={{ color: t.color }}>{t.label}</span>
                    </div>
                    <div className="mt-3 font-heading text-base font-bold leading-snug text-[#1F2937]">{l?.title || "—"}</div>
                    <p className="mt-1 flex-1 font-body text-xs text-[#6B7280]">{t.blurb}</p>
                    <div className="mt-1 text-[11px] font-semibold text-[#94A3B8]">{l?.activities ?? 0} activities</div>
                    {l && (
                      <div className="mt-3 flex gap-1.5">
                        <button onClick={() => onPreview(l)} data-testid={`tier-preview-${t.id}`} className="inline-flex flex-1 items-center justify-center gap-1 rounded-xl bg-white px-2 py-1.5 text-xs font-bold text-[#1F2937] shadow-sm hover:bg-[#F2ECE1]"><Eye size={13} /> Preview</button>
                        <button onClick={() => onAssign(l)} data-testid={`tier-assign-${t.id}`} className="inline-flex flex-1 items-center justify-center gap-1 rounded-xl px-2 py-1.5 text-xs font-bold text-white hover:brightness-95" style={{ background: t.color }}><Send size={13} /> Assign</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
